<template>
  <UikitButton
    v-if="filter?.name || name"
    :class="{
      'osk-filter-button': true,
      'osk-filter-button_active': isActive,
    }"
    light-outline
    size="s"
    :icon="icon"
    icon-color="black"
    :shadow-loading="shadowLoading"
    @click="handleClick"
  >
    {{ filter?.name ?? name }}
    <span
      v-if="selectedValuesCount"
      class="osk-filter-button__count"
    >
      ({{ selectedValuesCount }})
    </span>
  </UikitButton>
</template>

<script setup lang="ts">
import { ProductFilter } from '~/restAPI/data-contracts';

import {
  isBooleanFilter,
  isPriceFilter,
} from '~/utils/filters';

import type { TIconNames } from '~/assets/icons';

const props = defineProps<{
  filter?: ProductFilter
  selectedCount?: number
  name?: string
  icon?: TIconNames
  loading?: boolean
}>();

const emits = defineEmits<{
  (e: 'click', value?: ProductFilter): void,
}>();

const shadowLoading = ref(false);

const selectedValuesCount = computed(() => {
  const { filter, selectedCount } = props;

  if (!filter) return selectedCount;

  if (!isPriceFilter(filter) && !isBooleanFilter(filter)) return filter.selectedValues?.length ?? null;

  return null;
});

const isActive = computed(() => {
  const { filter, selectedCount } = props;

  if (!filter) return !!selectedCount;

  if (selectedValuesCount.value) return true;
  if (isBooleanFilter(filter)) return filter.value;

  return false;
});

function handleClick() {
  if (isBooleanFilter(props.filter)) {
    shadowLoading.value = true;
  }

  emits('click', props.filter);
}

watch(() => props.loading, (value) => {
  if (!value) shadowLoading.value = false;
});
</script>

<style scoped lang="scss">
@import "~/assets/scss/settings/index";

.osk-filter-button {
  flex-shrink: 0;

  &:hover {
    opacity: 1;
    border-color: $grey-3;
  }

  &:active {
    opacity: 0.6;
    border-color: $grey-3;
  }

  &_active {
    background: $grey-4;
    border-color: $grey-4 !important;
  }

  & + & {
    margin-left: $indent-compact;
  }

  &__count {
    margin-left: $indent-xsmall;
    font-weight: $font-weight-bold;
  }

  :deep(.osk-icon) {
    width: 24px;
    height: 24px;

    @include media-query(lg-and-up) {
      width: 18px;
      height: 18px;
    }
  }
}
</style>
